"use client";

import React from "react";

import { formatToHumanReadable } from "@/lib/format-data";
import { cx } from "@/lib/tailwind-utils";

import Input from "./input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./select";

export type DurationUnit = "months" | "weeks" | "days" | "hours" | "minutes";

const ALL_UNITS: DurationUnit[] = [
  "months",
  "weeks",
  "days",
  "hours",
  "minutes",
];

interface DurationInputProps extends Omit<
  React.ComponentPropsWithoutRef<typeof Input>,
  "onChange"
> {
  /** The value in minutes */
  value: number | undefined;
  /** Callback when the value changes (always in minutes) */
  onChange: (minutes: number | undefined) => void;
  /** Optional className for styling */
  className?: string;
  /** Optional className for styling the input */
  inputClassName?: string;
  /** Optional placeholder text */
  placeholder?: string;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Optional id for the input */
  id?: string;
  /** Optional minimum value in minutes */
  min?: number;
  /** Default unit to display (defaults to "days") */
  defaultUnit?: DurationUnit;
  /** Which units to show in the dropdown. Defaults to all units. */
  units?: DurationUnit[];
  /** Pins the unit shown, for callers that store the unit the user chose
   *  ("in 6 weeks"). Left unset, the input re-picks the "best" unit from the
   *  minutes on every change, so 6 weeks would show as 1.4 months. */
  unit?: DurationUnit;
  /** With `unit`: called when the user picks another unit, with the value
   *  converted into it, so the caller can store both in one update. */
  onUnitChange?: (unit: DurationUnit, minutes: number | undefined) => void;
}

const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY = 1440;
const MINUTES_PER_WEEK = 10080;
const MINUTES_PER_MONTH = 43200; // 30 days

/**
 * Returns true when `minutes / divisor` produces a "clean" value
 * (at most two decimal places), so e.g. 1.5 weeks is clean but
 * 1.142857 weeks is not.
 */
function isCleanDivision(minutes: number, divisor: number): boolean {
  const val = minutes / divisor;
  return Math.abs(val - Math.round(val * 100) / 100) < 0.0001;
}

/**
 * Determines the best unit to display a given number of minutes.
 */
function getBestUnit(minutes: number | undefined): DurationUnit {
  if (minutes == null) return "days";
  if (minutes < MINUTES_PER_HOUR) return "minutes";
  if (minutes < MINUTES_PER_DAY) return "hours";
  if (
    minutes >= MINUTES_PER_MONTH &&
    isCleanDivision(minutes, MINUTES_PER_MONTH)
  )
    return "months";
  if (minutes >= MINUTES_PER_WEEK && isCleanDivision(minutes, MINUTES_PER_WEEK))
    return "weeks";
  return "days";
}

/**
 * Converts minutes to the specified unit
 */
function convertToUnit(
  minutes: number | undefined,
  unit: DurationUnit,
): number | undefined {
  if (minutes === undefined || minutes === null) return undefined;
  switch (unit) {
    case "months":
      return minutes / MINUTES_PER_MONTH;
    case "weeks":
      return minutes / MINUTES_PER_WEEK;
    case "days":
      return minutes / MINUTES_PER_DAY;
    case "hours":
      return minutes / MINUTES_PER_HOUR;
    case "minutes":
      return minutes;
  }
}

/**
 * Converts from the specified unit to minutes
 */
function convertFromUnit(
  value: number | undefined,
  unit: DurationUnit,
): number | undefined {
  if (value === undefined || value === null || isNaN(value)) return undefined;
  switch (unit) {
    case "months":
      return Math.round(value * MINUTES_PER_MONTH);
    case "weeks":
      return Math.round(value * MINUTES_PER_WEEK);
    case "days":
      return Math.round(value * MINUTES_PER_DAY);
    case "hours":
      return Math.round(value * MINUTES_PER_HOUR);
    case "minutes":
      return Math.round(value);
  }
}

/**
 * A duration input component that allows users to input a duration in days, hours, or minutes.
 * The value is always stored and returned in minutes.
 */
export function DurationInput({
  value,
  onChange,
  className,
  placeholder = "0",
  disabled = false,
  id,
  min = 0,
  defaultUnit = "days",
  inputClassName,
  units = ALL_UNITS,
  unit: pinnedUnit,
  onUnitChange,
  ...props
}: DurationInputProps) {
  // Track whether the user has manually selected a unit
  const userSelectedUnit = React.useRef(false);
  const [autoUnit, setUnit] = React.useState<DurationUnit>(() => {
    // Initialize with the best unit for the initial value, or defaultUnit if no value
    if (value !== undefined && value !== null && value > 0) {
      const best = getBestUnit(value);
      // Never pick a unit the caller disallowed via `units` — fall back to
      // defaultUnit (which the caller keeps inside its own `units`).
      return units.includes(best) ? best : defaultUnit;
    }
    return defaultUnit;
  });
  const unit = pinnedUnit ?? autoUnit;
  const [displayValue, setDisplayValue] = React.useState<string>("");

  // Update display value and potentially unit when external value changes
  React.useEffect(() => {
    if (value === undefined || value === null) {
      setDisplayValue("");
      return;
    }

    // If user hasn't manually selected a unit, auto-select the best one — but
    // never one the caller disallowed via `units` (e.g. "minutes" for a 0 value
    // on a days/weeks/months input), which leaves the <Select> value unmatched
    // and renders blank. Fall back to defaultUnit.
    let currentUnit = unit;
    if (!userSelectedUnit.current && pinnedUnit === undefined) {
      const best = getBestUnit(value);
      const bestUnit = units.includes(best) ? best : defaultUnit;
      if (bestUnit !== unit) {
        currentUnit = bestUnit;
        setUnit(bestUnit);
      }
    }

    const converted = convertToUnit(value, currentUnit);
    if (converted !== undefined) {
      setDisplayValue(converted.toString());
    }
  }, [value, unit]);

  const handleUnitChange = (newUnit: DurationUnit) => {
    userSelectedUnit.current = true;
    // Convert current value to new unit for display, rounding up to whole number
    let roundedMinutes: number | undefined;
    if (value !== undefined && value !== null) {
      const converted = convertToUnit(value, newUnit);
      if (converted !== undefined) {
        const rounded = Math.ceil(converted);
        setDisplayValue(rounded.toString());
        roundedMinutes = convertFromUnit(rounded, newUnit);
      }
    }
    if (pinnedUnit !== undefined) {
      onUnitChange?.(newUnit, roundedMinutes);
      return;
    }
    setUnit(newUnit);
    // Update the value with the rounded conversion
    if (roundedMinutes !== undefined) {
      onChange(roundedMinutes);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setDisplayValue(inputValue);

    if (inputValue === "" || inputValue === undefined) {
      onChange(undefined);
      return;
    }

    const numValue = parseFloat(inputValue);
    if (isNaN(numValue)) {
      return;
    }

    const minutes = convertFromUnit(numValue, unit);
    if (minutes !== undefined) {
      // Apply minimum constraint
      const finalMinutes = Math.max(minutes, min);
      onChange(finalMinutes);
    }
  };

  const handleBlur = () => {
    // Round the display value to a reasonable precision
    if (displayValue !== "" && !isNaN(parseFloat(displayValue))) {
      const numValue = parseFloat(displayValue);
      const rounded =
        unit === "days" || unit === "weeks" || unit === "months"
          ? Math.round(numValue * 100) / 100
          : Math.round(numValue);
      setDisplayValue(rounded.toString());
    }
  };

  return (
    <div className={cx("flex items-center gap-1", className)}>
      <Input
        type="number"
        id={id}
        value={displayValue}
        onChange={handleInputChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        min={min}
        className={cx("w-20", inputClassName)}
        enableStepper={false}
        {...props}
      />
      <Select value={unit} onValueChange={handleUnitChange} disabled={disabled}>
        <SelectTrigger className="w-[120px]" disabled={disabled}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {units.map((u) => (
            <SelectItem key={u} value={u}>
              {/* "1 Week", not "1 Weeks" */}
              {formatToHumanReadable(
                parseFloat(displayValue) === 1 ? u.slice(0, -1) : u,
              )}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
